import React, { Children, cloneElement, type ForwardedRef, forwardRef, isValidElement, type ReactNode } from 'react';

import { parsePath } from 'history';
import { useSearchParams } from 'react-router-dom';

import { ENV_PRODUCTION } from '@pkg/utils';

import { StyledLink } from './styles';

export interface FilterLinkProps {
    // This should be the full route including search params: `?key=value`
    to: string;
    children: ReactNode;
    single?: boolean;
    isDefaultValue?: boolean;
}

// TODO: This component has been replaced by `<FilterAction />` and
// should be removed once ready.
function FilterLinkComponent(
    { to, children, isDefaultValue = false, single = false }: FilterLinkProps,
    ref: ForwardedRef<HTMLAnchorElement>,
) {
    const [searchParams] = useSearchParams();

    const path = parsePath(to);
    const toSearchParams = new URLSearchParams(path.search?.substring(1));
    const paramsKey = Array.from(toSearchParams.keys())[0];
    const paramsValue = Array.from(toSearchParams.values())[0];

    // Create new search parameters from the current search parameters.
    // Otherwise, we reference the same instance across components.
    const newSearchParams = new URLSearchParams(searchParams);

    if (!ENV_PRODUCTION && Boolean(!paramsKey || !paramsValue)) {
        // Throw an error if the environment is not production
        // and the `to` prop is missing the search parameters.
        throw new Error('FilterLink: paramsKey or paramsValue are not defined');
    }

    // Get all search parameter values for the given key.
    // Also handles multiple values in nested array.
    const allValuesForParamKey = newSearchParams.getAll(paramsKey).flatMap((value) => value.split(','));

    // Find params that match paramsValue in search params.
    const isActive: boolean =
        allValuesForParamKey?.some((value) => value === paramsValue) ||
        (allValuesForParamKey?.length === 0 && single && isDefaultValue);

    let replaceValues: string[] = [];

    if (isActive) {
        // Remove the paramsValue from the search params.
        replaceValues = allValuesForParamKey.filter((item) => item !== paramsValue);
    } else {
        // Otherwise, add the paramsValue to the existing search params.
        if (single && !isDefaultValue) {
            replaceValues = [paramsValue];
        } else if (!isDefaultValue) {
            replaceValues = [...allValuesForParamKey, paramsValue];
        }

        // TODO: Is it intentional that we do nothing if `isDefaultValue`?
    }

    if (replaceValues.length > 0) {
        // Replace the search params with the new values for the paramsKey.
        newSearchParams.set(paramsKey, replaceValues.join(','));
    } else {
        // If there are no values for the paramsKey we remove it from the search params.
        newSearchParams.delete(paramsKey);
    }

    // Update the path with the new search params.
    path.search = newSearchParams.toString();

    // Intentionally avoiding a `false` value for this prop by passing `undefined`.
    const ariaCurrent = isActive ? 'true' : undefined;

    interface ChildProps {
        active?: boolean;
        clickable?: boolean;
    }

    const childrenMarkup = Children.map(children, (child) =>
        // TODO - look into refactoring children map
        isValidElement(child)
            ? cloneElement(child, {
                  active: isActive,
                  clickable: true,
              } as ChildProps)
            : child,
    );

    // TODO: Why do we need to map over `children`?
    return (
        <StyledLink
            ref={ref}
            aria-current={ariaCurrent}
            to={{
                pathname: path.pathname,
                search: path.search,
            }}
        >
            {childrenMarkup}
        </StyledLink>
    );
}

export const FilterLink = forwardRef(FilterLinkComponent);
